"use client";

import React, { useState, useEffect } from "react";
import { X, FileText, Calendar, User, Package, DollarSign, Plus, Eye, Building2, Layout, Download, Truck, CreditCard } from "lucide-react";
import DynamicTemplateRenderer from "../../components/DynamicTemplateRenderer";
import { defaultQuotationTerms } from '../../constants/quotationTerms';
import html2pdf from 'html2pdf.js';
import templateService from '../../services/TemplateService';
import companyBranchService from '../../services/CompanyBranchService';
import { QuotationDataMapper } from '../../utils/QuotationDataMapper';
import { getProducts, addProduct, UNITS } from '../../constants/products';
 

function findProductForHsn(productName, productsList) {
  if (!productName || !Array.isArray(productsList) || productsList.length === 0) return null;
  const name = String(productName).trim();
  const nameLower = name.toLowerCase();
  const exact = productsList.find((p) => p.name.toLowerCase() === nameLower);
  if (exact) return exact;
  const normalized = nameLower.replace(/\s+/g, ' ').replace(/\s*sq\s*mm\s*$/i, ' sqmm').replace(/\s*s\s*$/i, ' sqmm');
  return productsList.find((p) => {
    const pNorm = p.name.toLowerCase().replace(/\s+/g, ' ').replace(/\s*sq\s*mm\s*$/i, ' sqmm');
    return pNorm === normalized || pNorm.includes(normalized) || normalized.includes(pNorm) || pNorm.startsWith(nameLower) || nameLower.startsWith(pNorm);
  }) || null;
}

/**
 * Ensures quotation table rows show HSN in the HSN column and quantity in the QUANTITY column.
 * Backend templates often use the same placeholder (e.g. {{this.hsn}}) in both cells; we fix per row.
 */
function fixQuotationTemplateHtml(html) {
  if (!html || typeof html !== 'string') return html;
  let out = html;
  // Replace second HSN placeholder in each table row with quantity (row-scoped, so we never span </tr>)
  out = out.replace(/(<tr[^>]*>)([\s\S]*?)(<\/tr>)/gi, (_match, openTag, rowContent, closeTag) => {
    let occurrence = 0;
    const fixedRow = rowContent.replace(/\{\{\s*this\.(hsn|hsnCode)\s*\}\}/g, (placeholder) => {
      occurrence += 1;
      return occurrence === 2 ? '{{this.quantity}}' : placeholder;
    });
    return openTag + fixedRow + closeTag;
  });
  // Legacy: some templates use {{this.length}} for quantity
  out = out.replace(/\{\{\s*this\.length\s*\}\}/g, '{{this.quantity}}');
  return out;
}

/** Single source for quotation live preview: context + template fix + render. */
function QuotationPreviewContent({ previewData, companyBranches, user, selectedTemplate, availableTemplates }) {
  const activeTemplate = availableTemplates?.find((t) => t.template_key === selectedTemplate);
  if (!activeTemplate?.html_content) return null;
  const context = QuotationDataMapper.prepareContext(previewData, companyBranches, user, selectedTemplate);
  const html = fixQuotationTemplateHtml(activeTemplate.html_content);

  return <DynamicTemplateRenderer html={html} data={context} containerId="quotation-content" />;
}

import apiClient from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../api/admin_api/api';
import productPriceService from '../../services/ProductPriceService';
import Toast from '../../utils/Toast';
import * as XLSX from 'xlsx';

function Card({ className, children }) {
  return <div className={`rounded-lg border bg-white shadow-sm ${className || ''}`}>{children}</div>;
}

function CardContent({ className, children }) {
  return <div className={`p-0 ${className || ''}`}>{children}</div>;
}

function CardHeader({ className, children }) {
  return <div className={`p-3 sm:p-4 md:p-6 ${className || ''}`}>{children}</div>;
}

function CardTitle({ className, children }) {
  return <h3 className={`text-lg font-semibold ${className || ''}`}>{children}</h3>;
}

function Button({ children, onClick, type = "button", variant = "default", size = "default", className = "" }) {
  const baseClasses = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-blue-500"
  };
  
  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-8 py-1 px-3 text-xs",
    icon: "h-10 w-10"
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const calculateTotals = (items, discountRate, taxRate) => {
  const subtotalRaw = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const subtotal = round2(subtotalRaw);
  const discountRateNum = parseFloat(discountRate) || 0;
  const taxRateNum = parseFloat(taxRate) || 0;
  const discountAmount = round2((subtotal * discountRateNum) / 100);
  const taxable = Math.max(0, subtotal - discountAmount);
  const taxAmount = round2((taxable * taxRateNum) / 100);
  const total = round2(taxable + taxAmount);

  return {
    subtotal,
    discountAmount,
    taxAmount,
    total
  };
};

export default function CreateQuotationForm({ customer, user, onClose, onSave, standalone = false, existingQuotation = null, creationMode = 'rfp' }) {
  const getSevenDaysLater = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  const [companyBranches, setCompanyBranches] = useState({});
  const [organizations, setOrganizations] = useState([]);

  const DEFAULT_BANK_DETAILS = {
    accountHolderName: "ANODE ELECTRIC PVT. LTD.",
    bankName: "ICICI Bank",
    branchName: "WRIGHT TOWN JABALPUR",
    accountNumber: "657605601783",
    ifscCode: "ICIC0006576"
  };

  const SAMRIDDHI_CABLE_BANK_DETAILS = {
    accountHolderName: "SAMRIDDHI CABLE INDUSTRIES PRIVATE LIMITED",
    bankName: "ICICI Bank",
    branchName: "Niwarganj Branch",
    accountNumber: "777705336601",
    ifscCode: "ICIC0007345"
  };

  const [hasEditedBankDetails, setHasEditedBankDetails] = useState(false);

  const getBankDetailsForOrgName = (orgName) => {
    const name = String(orgName || "").toUpperCase();
    // Match the organization name tokens only; org wording might vary (PVT./INDUSTRIES etc).
    const isSamriddhiCable =
      name.includes("SAMRIDDHI") &&
      name.includes("CABLE");

    return isSamriddhiCable ? SAMRIDDHI_CABLE_BANK_DETAILS : DEFAULT_BANK_DETAILS;
  };

  const [quotationData, setQuotationData] = useState({
    quotationNumber: `ANQ${Date.now().toString().slice(-6)}`,
    quotationDate: new Date().toISOString().split('T')[0],
    validUpto: getSevenDaysLater(new Date().toISOString().split('T')[0]),
    selectedBranch: '', 
    items: [
      {
        id: 1,
        productName: "",
        quantity: "",
        unit: "",
        companyRate: 0,
        buyerRate: "",
        amount: 0,
        hsn: "",
        remark: ""
      }
    ],
    subtotal: 0,
    discountRate: 0,
    discountAmount: 0,
    taxRate: 18,
    taxAmount: 0,
    total: 0,
    paymentMode: "",
    transportTc: "",
    dispatchThrough: "",
    deliveryTerms: "",
    materialType: "",
    remark: "",
    termsSections: defaultQuotationTerms.map(section => ({ ...section, points: [...section.points] })),
    billTo: {
      business: "",
      buyerName: "",
      address: "",
      phone: "",
      gstNo: "",
      state: ""
    },
    transportDetails: {
      lrNo: "",
      transport: "",
      transportId: "",
      vehicleNumber: ""
    },
    bankDetails: {
      ...DEFAULT_BANK_DETAILS
    }
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState({});
  const [rfpIdInput, setRfpIdInput] = useState('');
  const [validatingRfpId, setValidatingRfpId] = useState(false);
  const [validatedRfpDecision, setValidatedRfpDecision] = useState(null);
  const [rfpIdValidated, setRfpIdValidated] = useState(false);
  const needsRfpValidation = creationMode === 'rfp' && !existingQuotation;
  const [excelLoading, setExcelLoading] = useState(false);

  useEffect(() => {
    if (!customer) {
      return;
    }

    setQuotationData(prev => ({
      ...prev,
      customer: customer, 
      billTo: {
        business: (customer.business && customer.business !== 'N/A') ? customer.business : (customer.name || ""),
        buyerName: (customer.business && customer.business !== 'N/A') ? customer.business : (customer.name || ""), // Set Buyer Name same as Business Name
        address: (customer.address && customer.address !== 'N/A') ? customer.address : "",
        phone: customer.phone || "",
        gstNo: (customer.gstNo && customer.gstNo !== 'N/A' && customer.gstNo.trim() !== '') ? customer.gstNo : "URC",
        state: customer.state || ""
        }
    }));
  }, [customer]);

  useEffect(() => {
    if (existingQuotation && existingQuotation.id) {
      const dbQuotation = existingQuotation;
      
      const billTo = typeof dbQuotation.bill_to === 'string' 
        ? JSON.parse(dbQuotation.bill_to) 
        : (dbQuotation.bill_to || {});
      
      const bankDetails = typeof dbQuotation.bank_details === 'string'
        ? JSON.parse(dbQuotation.bank_details)
        : (dbQuotation.bank_details || {
            accountHolderName: "ANODE ELECTRIC PVT. LTD.",
            bankName: "ICICI Bank",
            branchName: "WRIGHT TOWN JABALPUR",
            accountNumber: "657605601783",
            ifscCode: "ICIC0006576"
          });
      
      const termsSections = typeof dbQuotation.terms_sections === 'string'
        ? JSON.parse(dbQuotation.terms_sections)
        : (dbQuotation.terms_sections || defaultQuotationTerms.map(section => ({ ...section, points: [...section.points] })));
      
      const items = (dbQuotation.items || []).map((item, index) => ({
        id: index + 1,
        productName: item.product_name || item.productName || "",
        quantity: item.quantity || "",
        unit: item.unit || "",
        companyRate: 0,
        buyerRate: item.unit_price || item.buyerRate || "",
        amount: item.taxable_amount || item.amount || 0,
        hsn: item.hsn_code || item.hsn || "",
        remark: item.remark || ""
      }));
      
      setQuotationData({
        quotationNumber: dbQuotation.quotation_number || `ANQ${Date.now().toString().slice(-6)}`,
        quotationDate: dbQuotation.quotation_date ? dbQuotation.quotation_date.split('T')[0] : new Date().toISOString().split('T')[0],
        validUpto: dbQuotation.valid_until ? dbQuotation.valid_until.split('T')[0] : getSevenDaysLater(new Date().toISOString().split('T')[0]),
        selectedBranch: dbQuotation.branch || '',
        items: items.length > 0 ? items : [{
          id: 1,
          productName: "",
          quantity: "",
          unit: "",
          companyRate: 0,
          buyerRate: "",
          amount: 0,
          hsn: "",
          remark: ""
        }],
        subtotal: parseFloat(dbQuotation.subtotal || 0),
        discountRate: parseFloat(dbQuotation.discount_rate || 0),
        discountAmount: parseFloat(dbQuotation.discount_amount || 0),
        taxRate: parseFloat(dbQuotation.tax_rate || 18),
        taxAmount: parseFloat(dbQuotation.tax_amount || 0),
        total: parseFloat(dbQuotation.total_amount || 0),
        paymentMode: dbQuotation.payment_mode || "",
        transportTc: dbQuotation.transport_tc || "",
        dispatchThrough: dbQuotation.dispatch_through || "",
        deliveryTerms: dbQuotation.delivery_terms || "",
        materialType: dbQuotation.material_type || "",
        remark: dbQuotation.remark || "",
        termsSections: termsSections,
        billTo: billTo,
        transportDetails: {
          lrNo: "",
          transport: "",
          transportId: "",
          vehicleNumber: ""
        },
        bankDetails: bankDetails
      });
      
      // Set template if available
      if (dbQuotation.template) {
        setSelectedTemplate(dbQuotation.template);
      }
    }
  }, [existingQuotation]);

  // Load products
  useEffect(() => {
    setProducts(getProducts());
  }, []);

  useEffect(() => {
    if (products.length === 0) return;
    const items = quotationData.items || [];
    let changed = false;
    const nextItems = items.map((item) => {
      const name = (item.productName || '').trim();
      if (!name || (item.hsn != null && String(item.hsn).trim() !== '')) return item;
      const match = findProductForHsn(name, products);
      if (match && match.hsn) {
        changed = true;
        return { ...item, hsn: match.hsn, unit: item.unit || match.defaultUnit || 'Mtr' };
      }
      return item;
    });
    if (changed) {
      setQuotationData((prev) => ({ ...prev, items: nextItems }));
    }
  }, [products.length, quotationData.items?.length]);

  // Load quotation templates from configuration
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await templateService.getTemplatesByType('quotation');
        setAvailableTemplates(templates);
        if (!selectedTemplate && templates.length > 0) {
          setSelectedTemplate(templates[0].template_key);
        }
      } catch (error) {
        console.error('Failed to load quotation templates:', error);
      }
    };

    loadTemplates();
  }, []);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const { branches, organizations: orgs } = await companyBranchService.fetchBranches();
        setCompanyBranches(branches);
        
        const sortedOrgs = [...orgs].sort((a, b) => {
          const nameA = (a.organization_name || a.legal_name || `Organization #${a.id}`).toLowerCase();
          const nameB = (b.organization_name || b.legal_name || `Organization #${b.id}`).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setOrganizations(sortedOrgs);

        if (!quotationData.selectedBranch && sortedOrgs.length > 0) {
          setQuotationData(prev => ({
            ...prev,
            selectedBranch: String(sortedOrgs[0].id)
          }));
        }
      } catch (error) {
        console.error('Failed to load organizations for quotation:', error);
      }
    };

    loadBranches();
  }, []);

  useEffect(() => {
    if (existingQuotation?.id) return;
    if (!quotationData.selectedBranch) return;
    if (hasEditedBankDetails) return;
    if (!companyBranches || Object.keys(companyBranches).length === 0) return;

    const branch = companyBranches[quotationData.selectedBranch];
    if (!branch) return;

    const nextBankDetails = getBankDetailsForOrgName(
      branch.name || branch.legalName || branch.description
    );

    setQuotationData(prev => ({
      ...prev,
      bankDetails: { ...nextBankDetails }
    }));
  }, [quotationData.selectedBranch, companyBranches, existingQuotation?.id, hasEditedBankDetails]);

  useEffect(() => {
    const storedRfpId = sessionStorage.getItem('pricingRfpDecisionId');
    if (storedRfpId && needsRfpValidation) {
      setRfpIdInput(storedRfpId);
      handleValidateRfpId(storedRfpId);
    }
  }, [needsRfpValidation]);

  const buildQuotationExcelTemplate = () => {
    const headers = ['Product Name', 'Quantity', 'Unit'];
    const hint = ['Use exact product spec', 'Number', 'Mtr / Nos / Kg'];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, hint, []]);
    ws['!cols'] = [{ wch: 40 }, { wch: 12 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Quotation Items');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Quotation_Items_Template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const applyItemsFromExcelFile = async (file) => {
    if (!file) return;
    setExcelLoading(true);
    try {
      const buf = await file.arrayBuffer();
      const workbook = XLSX.read(buf, { type: 'array', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
      if (!rows.length) {
        Toast.error('Excel file has no rows.');
        return;
      }

      const normKey = (k) => String(k || '').trim().toLowerCase();
      const resolveKey = (obj, keys) => {
        const found = Object.keys(obj).find((h) => keys.includes(normKey(h)));
        return found ? obj[found] : null;
      };

      const parsed = rows
        .map((row) => {
          const productName = resolveKey(row, ['product name', 'product spec', 'product', 'product_spec']);
          const quantityVal = resolveKey(row, ['quantity', 'qty', 'length']);
          const unitVal = resolveKey(row, ['unit', 'uom', 'quantity unit', 'length unit']);
          const productSpec = String(productName || '').trim();
          const qtyNum = quantityVal === '' || quantityVal == null ? NaN : Number.parseFloat(quantityVal);
          const quantity = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : NaN;
          const unit = String(unitVal || '').trim() || 'Mtr';
          if (!productSpec) return null;
          if (!Number.isFinite(quantity)) return { productSpec, quantity: null, unit };
          return { productSpec, quantity, unit };
        })
        .filter(Boolean);

      if (!parsed.length) {
        Toast.error('No valid product rows found. Check columns: Product Name, Quantity, Unit.');
        return;
      }

      const badQty = parsed.filter((r) => r.quantity == null);
      if (badQty.length) {
        Toast.error(`Invalid quantity in ${badQty.length} row(s). Fix and re-upload.`);
        return;
      }

      const uniqueSpecs = Array.from(new Set(parsed.map((r) => r.productSpec)));
      const pricePairs = await Promise.all(
        uniqueSpecs.map(async (spec) => {
          try {
            const res = await productPriceService.getApprovedPrice(spec);
            const unitPrice = res?.data ? Number.parseFloat(res.data.unit_price || 0) : NaN;
            return [spec, Number.isFinite(unitPrice) ? unitPrice : null];
          } catch {
            return [spec, null];
          }
        })
      );
      const priceBySpec = new Map(pricePairs);

      const nextItems = parsed.map((row, idx) => {
        const unitPrice = priceBySpec.get(row.productSpec);
        const buyerRate = unitPrice != null ? String(round2(unitPrice)) : '';
        const amount = unitPrice != null ? round2((Number(row.quantity) || 0) * unitPrice) : 0;
        const match = findProductForHsn(row.productSpec, products);
        return {
          id: idx + 1,
          productName: row.productSpec,
          quantity: String(row.quantity),
          unit: row.unit || (match?.defaultUnit || 'Mtr'),
          companyRate: unitPrice || 0,
          buyerRate,
          amount,
          hsn: (match?.hsn && String(match.hsn).trim()) || '',
          remark: unitPrice == null ? 'Price not found in approved list' : ''
        };
      });

      const totals = calculateTotals(nextItems, quotationData.discountRate, quotationData.taxRate);
      setQuotationData((prev) => ({
        ...prev,
        items: nextItems.length ? nextItems : prev.items,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        taxAmount: totals.taxAmount,
        total: totals.total
      }));

      const missing = nextItems.filter((i) => !i.buyerRate).length;
      if (missing) Toast.warning(`${missing} item(s) missing approved price. You can edit rates manually before submitting.`);
      Toast.success(`Loaded ${nextItems.length} item(s) from Excel.`);
    } catch (e) {
      Toast.error(e?.message || 'Failed to read Excel. Please check file format.');
    } finally {
      setExcelLoading(false);
    }
  };

  const handleValidateRfpId = async (rfpId = null) => {
    const idToValidate = rfpId || rfpIdInput.trim();
    if (!idToValidate) {
      Toast.error('Please enter RFP ID');
      return;
    }

    const currentLeadId = customer?.id ?? customer?.lead_id ?? null;
    const leadIdParam = currentLeadId != null ? `?leadId=${encodeURIComponent(currentLeadId)}` : '';

    setValidatingRfpId(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.PRICING_RFP_DECISION_GET(idToValidate) + leadIdParam);
      if (response.success && response.data) {
        const decision = response.data;
        const decisionLeadId = decision.lead_id ?? decision.leadId ?? decision.rfp_request?.lead_id;
        if (currentLeadId != null && decisionLeadId != null && String(decisionLeadId) !== String(currentLeadId)) {
          const leadName = customer?.name || customer?.business || 'this lead';
          Toast.error(`This RFP ID is linked to a different lead. Please enter the RFP ID that belongs to ${leadName} only.`);
          setValidatedRfpDecision(null);
          setRfpIdValidated(false);
          setValidatingRfpId(false);
          return;
        }
        setValidatedRfpDecision(decision);
        setRfpIdValidated(true);

        await populateQuotationFromRfpDecision(decision);

        Toast.success(`RFP ID validated successfully! Quotation populated with ${decision.products?.length || 0} product(s).`);
      } else {
        Toast.error('Invalid RFP ID. Please check and try again.');
        setValidatedRfpDecision(null);
        setRfpIdValidated(false);
      }
    } catch (error) {
      const data = error?.response?.data;
      const linkedToLeadName = data?.linkedToLeadName;
      const currentLeadName = customer?.name || customer?.business || 'this lead';
      let msg = data?.message || error.message;
      if (linkedToLeadName && !msg.includes(linkedToLeadName)) {
        msg = `This RFP ID is linked to ${linkedToLeadName}. Please enter the RFP ID that belongs to ${currentLeadName} only.`;
      }
      Toast.error(msg || 'Failed to validate RFP ID. Please check and try again.');
      setValidatedRfpDecision(null);
      setRfpIdValidated(false);
    } finally {
      setValidatingRfpId(false);
    }
  };

  const populateQuotationFromRfpDecision = async (decision) => {
    try {
      const rfpProducts = Array.isArray(decision.products) ? decision.products : [];

      let calculatorLog = decision.calculator_pricing_log || decision.rfp_request?.calculator_pricing_log || null;
      if (calculatorLog && typeof calculatorLog === 'string') {
        try {
          calculatorLog = JSON.parse(calculatorLog);
        } catch (e) {
        }
      }

      const hasCalculator = calculatorLog && typeof calculatorLog === 'object';
      
      const itemsWithPricing = await Promise.all(
        rfpProducts.map(async (product, index) => {
          let unitPrice = 0;
          let quantityForForm = '';
          let buyerRateForForm = '';
          let amount = 0;
          let remark = '';

          const productInfo = findProductForHsn(product.productSpec, products);
          const hsnForItem = (productInfo?.hsn && String(productInfo.hsn).trim()) || '8544';

          const log = (product.calculator_log && typeof product.calculator_log === 'object' ? product.calculator_log : null)
            || (index === 0 && hasCalculator ? calculatorLog : null);

          if (log && (log.totalPrice != null || log.calculatorTotalPrice != null)) {
            const lengthUsedRaw = log.quantity ?? log.length ?? product.quantity ?? product.length ?? '';
            const lengthNum = lengthUsedRaw === '' ? 0 : Number.parseFloat(lengthUsedRaw);
            const safeLength = Number.isFinite(lengthNum) ? lengthNum : 0;
            const totalFromCalculator = Number.parseFloat(log.totalPrice ?? log.calculatorTotalPrice ?? 0) || 0;

            unitPrice = safeLength > 0 ? round2(totalFromCalculator / safeLength) : 0;
            buyerRateForForm = unitPrice > 0 ? unitPrice.toFixed(2) : '0';
            quantityForForm = safeLength ? String(safeLength) : '';
            amount = round2(totalFromCalculator || (safeLength * unitPrice));

          } else {
            try {
              const priceRes = await productPriceService.getApprovedPrice(product.productSpec);
              if (priceRes?.data) {
                unitPrice = parseFloat(priceRes.data.unit_price || 0);
              }
            } catch (error) {
              if (error?.status !== 404) {
                console.error(`Failed to fetch price for ${product.productSpec}:`, error);
              }
            }

            const rawQty = product.quantity ?? product.qty ?? product.length ?? '';
            const qtyNum = rawQty === '' ? 0 : Number.parseFloat(rawQty);
            const qtyInt = Number.isFinite(qtyNum) ? Math.trunc(qtyNum) : 0;
            quantityForForm = rawQty === '' ? '' : String(qtyInt);

            const targetTotal = product.targetPrice != null && product.targetPrice !== '' ? Number.parseFloat(product.targetPrice) : NaN;
            if (Number.isFinite(targetTotal) && qtyInt > 0) {
              unitPrice = round2(targetTotal / qtyInt);
              buyerRateForForm = unitPrice.toFixed(2);
              amount = round2(targetTotal);
            } else {
              amount = round2((qtyInt || 0) * unitPrice);
              buyerRateForForm = unitPrice > 0 ? unitPrice.toString() : '';
            }
          }

          const prodLog = product.calculator_log && typeof product.calculator_log === 'object' ? product.calculator_log : (index === 0 && hasCalculator ? calculatorLog : null);
          return {
            id: index + 1,
            productName: product.productSpec,
            quantity: quantityForForm,
            unit: product.lengthUnit || product.quantityUnit || prodLog?.lengthUnit || prodLog?.quantityUnit || 'Mtr',
            companyRate: unitPrice,
            buyerRate: buyerRateForForm,
            amount: amount,
            hsn: hsnForItem,
            remark
          };
        })
      );
      
      // Calculate totals
      const totals = calculateTotals(
        itemsWithPricing,
        quotationData.discountRate,
        quotationData.taxRate
      );
      
      // Update quotation data
      setQuotationData(prev => ({
        ...prev,
        items: itemsWithPricing.length > 0 ? itemsWithPricing : prev.items,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        taxAmount: totals.taxAmount,
        total: totals.total,
        deliveryTerms: decision.delivery_timeline 
          ? `Delivery required by: ${new Date(decision.delivery_timeline).toLocaleDateString('en-IN')}`
          : prev.deliveryTerms,
        remark: decision.special_requirements || prev.remark
      }));
    } catch (error) {
      console.error('Error populating quotation from RFP decision:', error);
      alert('Failed to populate quotation. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    const newData = {
      ...quotationData,
      [field]: value
    };

    // If quotationDate changes, update validUpto to be 7 days later
    if (field === 'quotationDate') {
      newData.validUpto = getSevenDaysLater(value);
    }

    // Recalculate totals when discount or tax changes
    if (field === 'discountRate' || field === 'taxRate') {
      const totals = calculateTotals(
        newData.items,
        newData.discountRate,
        newData.taxRate
      );
      newData.subtotal = totals.subtotal;
      newData.discountAmount = totals.discountAmount;
      newData.taxAmount = totals.taxAmount;
      newData.total = totals.total;
    }

    setQuotationData(newData);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...quotationData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    }
    
    if (field === 'productName' && value) {
      const matchingProduct = findProductForHsn(value, products);
      if (matchingProduct) {
        updatedItems[index].hsn = matchingProduct.hsn || '';
        if (!updatedItems[index].unit) {
          updatedItems[index].unit = matchingProduct.defaultUnit || 'Mtr';
        }
      } else {
        const currentHsn = updatedItems[index].hsn || '';
        const currentUnit = updatedItems[index].unit || 'Mtr';
        addProduct(value, currentHsn, currentUnit);
        setProducts(getProducts());
      }
    }
    
    if (['quantity', 'companyRate', 'buyerRate'].includes(field)) {
      const qty = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].buyerRate) || 0;
      updatedItems[index].amount = round2(qty * rate);
    }
    
    const totals = calculateTotals(
      updatedItems,
      quotationData.discountRate,
      quotationData.taxRate
    );
    
    setQuotationData(prev => ({
      ...prev,
      items: updatedItems,
      subtotal: totals.subtotal,
      discountAmount: totals.discountAmount,
      taxAmount: totals.taxAmount,
      total: totals.total
    }));
  };

  const handleTermTitleChange = (index, value) => {
    setQuotationData(prev => {
      const termsSections = (prev.termsSections || []).map((section, idx) =>
        idx === index ? { ...section, title: value } : section
      )
      return { ...prev, termsSections }
    })
  }

  const handleTermPointsChange = (index, value) => {
    const points = value
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)

    setQuotationData(prev => {
      const termsSections = (prev.termsSections || []).map((section, idx) =>
        idx === index ? { ...section, points } : section
      )
      return { ...prev, termsSections }
    })
  }

  const resetTermsToDefault = () => {
    setQuotationData(prev => ({
      ...prev,
      termsSections: defaultQuotationTerms.map(section => ({
        title: section.title,
        points: [...section.points]
      }))
    }));
  };

  const handleTransportDetailsChange = (field, value) => {
    setQuotationData(prev => ({
      ...prev,
      transportDetails: {
        ...prev.transportDetails,
        [field]: value
      }
    }));
  };

  const handleBankDetailsChange = (field, value) => {
    setHasEditedBankDetails(true);
    setQuotationData(prev => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value
      }
    }));
  };

  const addItem = () => {
    setQuotationData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: prev.items.length + 1,
        productName: "",
        quantity: "",
        unit: "",
        companyRate: 0,
        buyerRate: "",
        amount: 0,
        hsn: "",
        remark: ""
      }]
    }));
  };

  const removeItem = (index) => {
    if (quotationData.items.length > 1) {
      const updatedItems = quotationData.items.filter((_, i) => i !== index);
      const totals = calculateTotals(
        updatedItems,
        quotationData.discountRate,
        quotationData.taxRate
      );
      
      setQuotationData(prev => ({
        ...prev,
        items: updatedItems,
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount,
        taxAmount: totals.taxAmount,
        total: totals.total
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // RFP ID validation is mandatory
    if (needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)) {
      Toast.error('Please enter and validate RFP ID before creating quotation.');
      return;
    }

    if (!selectedTemplate) {
      alert('Please select a quotation template.');
      return;
    }

    try {
      await onSave({
        ...quotationData,
        customer,
        createdAt: new Date().toISOString(),
        template: selectedTemplate,
        creationMode,
        quotationId: existingQuotation?.id || null // Pass quotation ID if editing
      });
      // Let parent decide whether to close (onSave may handle it)
      if (!standalone && typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save quotation:', error);
      alert('Failed to save quotation. Please try again.');
    }
  };

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState({});

  // Update preview data when form data changes
  useEffect(() => {
    setPreviewData(quotationData);
  }, [quotationData]);


  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Download PDF function that uses the selected template
  const handleDownloadPDF = async () => {
    try {
      // Find the currently rendered quotation content in the preview modal
      const quotationContent = document.getElementById('quotation-content');
      
      if (!quotationContent) {
        alert('Quotation preview not found. Please try again.');
        return;
      }

      // Temporarily hide the signatory section if needed
      const signatorySection = quotationContent.querySelector('.quotation-signatory-section');
      let wasVisible = true;
      if (signatorySection) {
        wasVisible = signatorySection.style.display !== 'none';
        // Keep it visible for PDF - no need to hide
      }

      // PDF options - Optimized for single page A4, HD quality, and color preservation
      const opt = {
        margin: [0.05, 0.05, 0.05, 0.05], // Minimal margins for maximum space
        filename: `Quotation-${previewData?.quotationNumber || 'Draft'}-${(previewData?.billTo?.business || 'Customer').replace(/\s+/g, '-')}.pdf`,
        image: { type: 'png', quality: 1.0 }, // PNG preserves colors better than JPEG
        html2canvas: { 
          scale: 3.5, // High scale for HD quality (prevents blurry text)
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          removeContainer: false, // Preserve container for better rendering
          foreignObjectRendering: false, // Better color preservation
          imageTimeout: 0, // No timeout for images
          onclone: (clonedDoc) => {
            // Inject optimized CSS to fit content on single A4 page and preserve colors
            const style = clonedDoc.createElement('style');
            style.textContent = `
              * {
                box-sizing: border-box;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              #quotation-content {
                width: 210mm !important;
                max-height: 297mm !important;
                overflow: hidden !important;
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .page {
                width: 210mm !important;
                min-height: auto !important;
                max-height: 297mm !important;
                padding: 6px 12px 8px !important;
                margin: 0 !important;
                overflow: hidden !important;
                page-break-inside: avoid !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .mt-8 { margin-top: 3px !important; }
              .mt-16 { margin-top: 5px !important; }
              .top-title { 
                padding: 5px 0 !important; 
                font-size: 15px !important;
                margin-bottom: 3px !important;
                background: #87CEEB !important;
                background-color: #87CEEB !important;
                color: #fff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .section-header { 
                padding: 3px 0 !important; 
                font-size: 10px !important;
                margin-top: 4px !important;
                background: #87CEEB !important;
                background-color: #87CEEB !important;
                color: #fff !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .sub-header {
                background: #E0F2FE !important;
                background-color: #E0F2FE !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .product-header th {
                background: #E0F2FE !important;
                background-color: #E0F2FE !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .summary-label {
                background: #E0F2FE !important;
                background-color: #E0F2FE !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .small { font-size: 8.5px !important; }
              .terms-section { margin-top: 3px !important; }
              .terms-title { margin-top: 6px !important; }
              .terms-section p { 
                margin: 0.5px 0 !important; 
                font-size: 7.5px !important;
                line-height: 1.15 !important;
              }
              .terms-section-title { 
                font-size: 8.5px !important;
                margin-bottom: 0.5px !important;
              }
              .prepared-by { margin-top: 12px !important; }
              .bordered td, .bordered th { 
                padding: 1.5px 3px !important; 
                font-size: 8.5px !important;
              }
              .product-remark {
                font-size: 7.5px !important;
                margin-top: 0.5px !important;
              }
              table { 
                font-size: 8.5px !important;
              }
              .company-block p { 
                margin: 0.5px 0 !important; 
                font-size: 8.5px !important;
              }
              .info-box td { 
                padding: 1.5px 3px !important; 
                font-size: 8.5px !important;
              }
              .info-title {
                background: #E0F2FE !important;
                background-color: #E0F2FE !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .logo-box {
                width: 100px !important;
                height: 80px !important;
              }
              .company-name {
                font-size: 12px !important;
              }
            `;
            clonedDoc.head.appendChild(style);
            
            // Ensure content fits on single page and force colors
            const clonedElement = clonedDoc.getElementById('quotation-content');
            if (clonedElement) {
              clonedElement.style.width = '210mm';
              clonedElement.style.maxHeight = '297mm';
              clonedElement.style.overflow = 'hidden';
              clonedElement.style.webkitPrintColorAdjust = 'exact';
              clonedElement.style.printColorAdjust = 'exact';
              
              // Find and optimize the page element
              const pageElement = clonedElement.querySelector('.page');
              if (pageElement) {
                pageElement.style.width = '210mm';
                pageElement.style.maxHeight = '297mm';
                pageElement.style.overflow = 'hidden';
                pageElement.style.pageBreakInside = 'avoid';
                pageElement.style.webkitPrintColorAdjust = 'exact';
                pageElement.style.printColorAdjust = 'exact';
              }
              
              // Force background colors as inline styles on all colored elements
              const topTitles = clonedElement.querySelectorAll('.top-title');
              topTitles.forEach(el => {
                el.style.background = '#87CEEB';
                el.style.backgroundColor = '#87CEEB';
                el.style.color = '#fff';
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
              });
              
              const sectionHeaders = clonedElement.querySelectorAll('.section-header');
              sectionHeaders.forEach(el => {
                el.style.background = '#87CEEB';
                el.style.backgroundColor = '#87CEEB';
                el.style.color = '#fff';
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
              });
              
              const subHeaders = clonedElement.querySelectorAll('.sub-header, .info-title');
              subHeaders.forEach(el => {
                el.style.background = '#E0F2FE';
                el.style.backgroundColor = '#E0F2FE';
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
              });
              
              const productHeaders = clonedElement.querySelectorAll('.product-header th');
              productHeaders.forEach(el => {
                el.style.background = '#E0F2FE';
                el.style.backgroundColor = '#E0F2FE';
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
              });
              
              const summaryLabels = clonedElement.querySelectorAll('.summary-label');
              summaryLabels.forEach(el => {
                el.style.background = '#E0F2FE';
                el.style.backgroundColor = '#E0F2FE';
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
              });
              
              // Force color preservation on all elements
              const allElements = clonedElement.querySelectorAll('*');
              allElements.forEach(el => {
                el.style.webkitPrintColorAdjust = 'exact';
                el.style.printColorAdjust = 'exact';
                el.style.colorAdjust = 'exact';
              });
            }
          }
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: false, // Disable compression to preserve quality and colors
          putOnlyUsedFonts: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } // Prevent page breaks
      };

      // Generate and download PDF from the currently visible template
      await html2pdf().set(opt).from(quotationContent).save();

      // Restore signatory section visibility if it was changed
      if (signatorySection && !wasVisible) {
        signatorySection.style.display = '';
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const { billTo, items } = quotationData;
    
    // Check bill-to information
    if (!billTo.business || !billTo.phone || !billTo.address || !billTo.state) {
      return false;
    }
    
    // Check items
    if (items.length === 0) {
      return false;
    }
    
    return items.every(item => 
      item.productName && 
      item.quantity > 0 && 
      item.buyerRate > 0
    );
  };


  if (showPreview) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-2 sm:p-4">
        <div className="w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative bg-white rounded-lg shadow-xl">
          {/* Header with Template Selector */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 z-10">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Quotation Preview</h2>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Template Selector */}
              <div className="flex gap-2">
                {availableTemplates.map((template) => (
                <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.template_key)}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                      selectedTemplate === template.template_key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                    title={template.description || template.name}
                >
                    {template.name}
                </button>
                ))}
              </div>
          {/* Close button */}
          <button
            onClick={togglePreview}
                className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            title="Close Preview"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
            </div>
          </div>
          
          {/* Preview Content - full width of modal */}
          <div className="p-3 sm:p-6 w-full">
            <QuotationPreviewContent
              previewData={previewData}
              companyBranches={companyBranches}
              user={user}
              selectedTemplate={selectedTemplate}
              availableTemplates={availableTemplates}
            />
          </div>
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <Button 
              type="button" 
              onClick={handleDownloadPDF}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
            <Button 
              type="button" 
              onClick={() => {
                // RFP ID validation is mandatory only for RFP mode
                if (needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)) {
                  Toast.error('Please validate RFP ID before saving quotation.');
                  return;
                }
                onSave({
                  ...previewData,
                  template: selectedTemplate // Include the selected template
                });
              }}
              disabled={needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)}
              className={`bg-green-600 hover:bg-green-700 ${
                needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              {needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision) 
                ? 'Validate RFP ID First' 
                : 'Save Quotation'}
            </Button>
          </div>
        </div>
      </div>
    );
  }


  const formContent = (
    <>
      <CardHeader className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 pb-3 border-b ${standalone ? 'pt-3 sm:pt-6' : ''}`}>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-sm">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg sm:text-xl font-semibold text-gray-900">{existingQuotation ? 'Edit Quotation' : 'Create Quotation'}</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600">For {customer?.name}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-gray-100">
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>

      <div className="flex flex-col lg:flex-row gap-4 p-3 sm:p-4 md:p-6" style={{ minHeight: 'calc(100vh - 200px)' }}>
        {/* Left Side - Form */}
        <div className="flex-1 overflow-y-auto pr-4" style={{ maxHeight: 'calc(100vh - 200px)', minWidth: '60%' }}>
          <CardContent className="p-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* RFP ID Validation Box - Already validated before opening this modal */}
            {needsRfpValidation && (
              <div className={`p-4 border-2 rounded-lg ${rfpIdValidated ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                {customer && (
                  <p className="text-sm font-medium text-blue-700 mb-2">
                    Quotation for lead: <span className="font-semibold">{customer.name || customer.business || '—'}</span>
                    {customer.business && customer.name !== customer.business && (
                      <span className="text-gray-600"> ({customer.business})</span>
                    )}
                  </p>
                )}
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  RFP ID <span className="text-red-600">*</span> (Required)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={rfpIdInput}
                    onChange={(e) => setRfpIdInput(e.target.value)}
                    placeholder="Enter RFP ID (e.g., RFP-202412-0001)"
                    className={`flex-1 px-3 py-2 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 ${
                      rfpIdValidated 
                        ? 'border-green-400 focus:ring-green-500 focus:border-green-500 bg-green-50' 
                        : 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                    disabled={validatingRfpId || rfpIdValidated}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => handleValidateRfpId()}
                    disabled={validatingRfpId || rfpIdValidated || !rfpIdInput.trim()}
                    className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium ${
                      rfpIdValidated 
                        ? 'bg-green-600 hover:bg-green-500' 
                        : 'bg-blue-600 hover:bg-blue-500'
                    }`}
                  >
                    {validatingRfpId ? 'Validating...' : rfpIdValidated ? '✓ Validated' : 'Validate'}
                  </button>
                </div>
                {!rfpIdValidated && (
                  <p className="mt-2 text-xs text-red-600 font-medium">
                    ⚠ RFP ID validation is required to create quotation. Please enter and validate your RFP ID.
                  </p>
                )}
                {rfpIdValidated && validatedRfpDecision && (
                  <p className="mt-2 text-xs text-green-600 font-medium">
                    ✓ RFP ID validated successfully! Quotation populated with {validatedRfpDecision.products?.length || 0} product(s).
                  </p>
                )}
              </div>
            )}

            {creationMode === 'price_list' && !existingQuotation && (
              <div className="p-4 border-2 rounded-lg bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Via Price List (Excel Upload)</p>
                    <p className="text-xs text-blue-700">Upload items Excel. Rates are auto-filled from approved price list.</p>
                  </div>
                  <button
                    type="button"
                    onClick={buildQuotationExcelTemplate}
                    className="px-3 py-2 text-xs font-semibold rounded-lg border border-blue-300 text-blue-800 bg-white hover:bg-blue-100"
                  >
                    Download Template
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    disabled={excelLoading}
                    onChange={(e) => applyItemsFromExcelFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm"
                  />
                  <span className="text-xs text-blue-700 whitespace-nowrap">{excelLoading ? 'Loading…' : ''}</span>
                </div>
              </div>
            )}
            
            {/* Quotation Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <div className="p-1 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md">
                    <FileText className="h-3.5 w-3.5 text-white" />
                  </div>
                  Quotation Number
                </label>
                <input
                  type="text"
                  value={quotationData.quotationNumber}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600 cursor-not-allowed"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <div className="p-1 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md">
                    <Calendar className="h-3.5 w-3.5 text-white" />
                  </div>
                  Quotation Date *
                </label>
                <input
                  type="date"
                  required
                  value={quotationData.quotationDate}
                  onChange={(e) => handleInputChange("quotationDate", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <div className="p-1 bg-gradient-to-br from-green-500 to-green-600 rounded-md">
                    <Calendar className="h-3.5 w-3.5 text-white" />
                  </div>
                  Valid Until *
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600">
                  {quotationData.validUpto} (7 days from quotation date)
                </div>
                <input
                  type="hidden"
                  value={quotationData.validUpto}
                  name="validUpto"
                />
              </div>
            </div>

            {/* Branch Selection */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-md">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                Company Branch
              </h3>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Select Branch *</label>
                <select
                  value={quotationData.selectedBranch}
                  onChange={(e) => handleInputChange("selectedBranch", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Select Organization</option>
                  {[...organizations].sort((a, b) => {
                    const nameA = (a.organization_name || a.legal_name || `Organization #${a.id}`).toLowerCase();
                    const nameB = (b.organization_name || b.legal_name || `Organization #${b.id}`).toLowerCase();
                    return nameA.localeCompare(nameB);
                  }).map((org) => (
                    <option key={org.id} value={String(org.id)}>
                      {org.organization_name || org.legal_name || `Organization #${org.id}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  Selected branch will determine the letterhead and company details for this quotation.
                </p>
              </div>
            </div>

            {/* Bill To Section */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-md">
                  <User className="h-4 w-4 text-white" />
                </div>
                Bill To Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Business Name *</label>
                  <input
                    type="text"
                    required
                    value={quotationData.billTo.business}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, business: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Buyer Name *</label>
                  <input
                    type="text"
                    required
                    value={quotationData.billTo.buyerName}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, buyerName: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Phone *</label>
                  <input
                    type="text"
                    required
                    value={quotationData.billTo.phone}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, phone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Address *</label>
                  <input
                    type="text"
                    required
                    value={quotationData.billTo.address}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, address: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">GST Number</label>
                  <input
                    type="text"
                    value={quotationData.billTo.gstNo}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, gstNo: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">State *</label>
                  <input
                    type="text"
                    required
                    value={quotationData.billTo.state}
                    onChange={(e) => setQuotationData(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, state: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <div className="p-1.5 bg-gradient-to-br from-orange-500 to-amber-600 rounded-md">
                    <Package className="h-4 w-4 text-white" />
                  </div>
                  Product Details
                </h3>
                {!rfpIdValidated && (
                  <Button type="button" onClick={addItem} size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                )}
              </div>

              <div className="border border-gray-300 rounded-lg overflow-x-auto bg-white">
                <table className="w-full min-w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '28%' }}>Product Name</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '12%' }}>HSN/SAC</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '12%' }}>Qty</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '10%' }}>Unit</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '15%' }}>Buyer Rate</th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase" style={{ width: '15%' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {quotationData.items.map((item, index) => (
                      <React.Fragment key={item.id || index}>
                        <tr>
                          <td className="px-2 py-3">
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Product name"
                                value={item.productName}
                                onChange={(e) => {
                                  if (rfpIdValidated) return;
                                  setProductSearchTerm(prev => ({ ...prev, [index]: e.target.value }));
                                  handleItemChange(index, 'productName', e.target.value);
                                }}
                                onFocus={() => {
                                  if (rfpIdValidated) return;
                                  setProductSearchTerm(prev => ({ ...prev, [index]: item.productName || '' }));
                                }}
                                onBlur={() => {
                                  if (rfpIdValidated) return;
                                  setTimeout(() => setProductSearchTerm(prev => {
                                    const newState = { ...prev };
                                    delete newState[index];
                                    return newState;
                                  }), 200);
                                }}
                                className={`w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none ${rfpIdValidated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:ring-1 focus:ring-blue-500'}`}
                                required
                                list={rfpIdValidated ? undefined : `product-list-${index}`}
                                disabled={rfpIdValidated}
                              />
                              <datalist id={`product-list-${index}`}>
                                {products
                                  .filter(p => !productSearchTerm[index] || 
                                    p.name.toLowerCase().includes(productSearchTerm[index].toLowerCase()))
                                  .slice(0, 20)
                                  .map((product, idx) => (
                                    <option key={idx} value={product.name} />
                                  ))}
                              </datalist>
                            </div>
                          </td>
                          <td className="px-2 py-3">
                            <input
                              type="text"
                              placeholder="HSN/SAC"
                              value={item.hsn || ''}
                              onChange={(e) => handleItemChange(index, 'hsn', e.target.value)}
                              className={`w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono text-xs focus:outline-none ${rfpIdValidated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:ring-1 focus:ring-purple-500'}`}
                              disabled={rfpIdValidated}
                            />
                          </td>
                          <td className="px-2 py-3">
                            <input
                              type="number"
                              min="0"
                              placeholder="Qty"
                              value={item.quantity || ''}
                              onChange={(e) => {
                                if (rfpIdValidated) return;
                                handleItemChange(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value) || '');
                              }}
                              className={`w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none ${rfpIdValidated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:ring-1 focus:ring-purple-500'}`}
                              disabled={rfpIdValidated}
                            />
                          </td>
                          <td className="px-2 py-3">
                            <select
                              value={item.unit || ''}
                              onChange={(e) => {
                                if (rfpIdValidated) return;
                                handleItemChange(index, 'unit', e.target.value);
                              }}
                              className={`w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none ${rfpIdValidated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:ring-1 focus:ring-purple-500'}`}
                              disabled={rfpIdValidated}
                            >
                              <option value="">Select</option>
                              {UNITS.map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-3">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Rate"
                                value={item.buyerRate || ''}
                                onChange={(e) => {
                                  if (rfpIdValidated) return;
                                  handleItemChange(index, 'buyerRate', e.target.value === '' ? '' : parseFloat(e.target.value) || '');
                                }}
                                className={`w-full px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none ${rfpIdValidated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'focus:ring-1 focus:ring-purple-500'}`}
                                disabled={rfpIdValidated}
                              />
                          </td>
                          <td className="px-2 py-3 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                            ₹{parseFloat(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                        <tr key={`remark-${item.id}`} className="bg-gray-50">
                          <td colSpan="6" className="px-2 py-2">
                            <input
                              type="text"
                              placeholder="Enter remark for this product (optional)"
                              value={item.remark || ''}
                              onChange={(e) => handleItemChange(index, 'remark', e.target.value)}
                              className={`w-full px-2 py-1 border border-gray-200 rounded text-sm italic text-gray-600 focus:outline-none ${rfpIdValidated ? 'bg-gray-50' : 'focus:ring-1 focus:ring-purple-500'}`}
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>₹{(Number(quotationData.subtotal) || 0).toFixed(2)}</span>
                  </div>
              <div className="flex justify-between text-sm items-center gap-2">
                <span>Discount (%):</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={quotationData.discountRate}
                    onChange={(e) => handleInputChange('discountRate', e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 text-right"
                  />
                  <span className="text-gray-600">₹{(Number(quotationData.discountAmount) || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span>GST (18%):</span>
                <span>₹{(Number(quotationData.taxAmount) || 0).toFixed(2)}</span>
              </div>
                  <div className="flex justify-between text-lg font-semibold border-t pt-2">
                    <span>Total:</span>
                    <span>₹{(Number(quotationData.total) || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Transport & Delivery Terms - New Section */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-md">
                  <Truck className="h-4 w-4 text-white" />
                </div>
                Delivery & Payment Terms
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Mode/Term & Payment</label>
                  <input
                    type="text"
                    value={quotationData.paymentMode}
                    onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Advance, 30 days credit"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Transport T&C</label>
                  <input
                    type="text"
                    value={quotationData.transportTc}
                    onChange={(e) => handleInputChange('transportTc', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. To Pay, Paid"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Dispatch Through</label>
                  <input
                    type="text"
                    value={quotationData.dispatchThrough}
                    onChange={(e) => handleInputChange('dispatchThrough', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Road, Transport Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Terms of Delivery</label>
                  <input
                    type="text"
                    value={quotationData.deliveryTerms}
                    onChange={(e) => handleInputChange('deliveryTerms', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Within 7 days"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Material Type</label>
                  <input
                    type="text"
                    value={quotationData.materialType}
                    onChange={(e) => handleInputChange('materialType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Original, Spare"
                  />
                </div>
              </div>
            </div>

            {/* Transport Details */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-md">
                  <Truck className="h-4 w-4 text-white" />
                </div>
                Transport Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">L.R. No</label>
                  <input
                    type="text"
                    value={quotationData.transportDetails.lrNo}
                    onChange={(e) => handleTransportDetailsChange('lrNo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter L.R. Number"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Transport Name</label>
                  <input
                    type="text"
                    value={quotationData.transportDetails.transport}
                    onChange={(e) => handleTransportDetailsChange('transport', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter Transport Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Transport ID</label>
                  <input
                    type="text"
                    value={quotationData.transportDetails.transportId}
                    onChange={(e) => handleTransportDetailsChange('transportId', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter Transport ID"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Vehicle Number</label>
                  <input
                    type="text"
                    value={quotationData.transportDetails.vehicleNumber}
                    onChange={(e) => handleTransportDetailsChange('vehicleNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter Vehicle Number"
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <div className="p-1.5 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-md">
                  <CreditCard className="h-4 w-4 text-white" />
                </div>
                Bank Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Account Holder Name</label>
                  <input
                    type="text"
                    value={quotationData.bankDetails.accountHolderName || ''}
                    onChange={(e) => handleBankDetailsChange('accountHolderName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter Account Holder Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Bank Name</label>
                  <input
                    type="text"
                    value={quotationData.bankDetails.bankName}
                    onChange={(e) => handleBankDetailsChange('bankName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter Bank Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Branch Name</label>
                  <input
                    type="text"
                    value={quotationData.bankDetails.branchName}
                    onChange={(e) => handleBankDetailsChange('branchName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter Branch Name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Account Number</label>
                  <input
                    type="text"
                    value={quotationData.bankDetails.accountNumber}
                    onChange={(e) => handleBankDetailsChange('accountNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter Account Number"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">IFSC Code</label>
                  <input
                    type="text"
                    value={quotationData.bankDetails.ifscCode}
                    onChange={(e) => handleBankDetailsChange('ifscCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter IFSC Code"
                  />
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md">
                    <FileText className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-base font-semibold text-gray-900">Terms & Conditions</span>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={resetTermsToDefault}>
                  Reset to Default
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Edit headings and bullet points below. Each new line becomes a bullet in the quotation preview.
              </p>
              <div className="space-y-3">
                {(quotationData.termsSections || []).map((section, sectionIndex) => (
                  <div key={sectionIndex} className="border border-gray-300 rounded-lg bg-white">
                    <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => handleTermTitleChange(sectionIndex, e.target.value)}
                        className="w-full text-sm font-semibold text-gray-800 bg-transparent border-none focus:outline-none focus:ring-0"
                        placeholder="Section title"
                      />
                    </div>
                    <div className="p-3">
              <textarea
                        value={section.points.join('\n')}
                        onChange={(e) => handleTermPointsChange(sectionIndex, e.target.value)}
                        rows={Math.max(3, section.points.length)}
                        className="w-full text-xs text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter each bullet point on a new line"
              />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end pt-4 border-t">
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)}
                  className={`bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white ${
                    needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  title={needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision) ? 'Please validate RFP ID first' : ''}
                >
                  {needsRfpValidation && (!rfpIdValidated || !validatedRfpDecision) 
                    ? 'Validate RFP ID First' 
                    : 'Save Quotation'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
        </div>

        {/* Right Side - Live Preview */}
        <div className="flex-1 min-w-[320px] border-l pl-4" style={{ maxWidth: '640px' }}>
          <div className="sticky top-4">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <div className="p-1.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-md">
                    <Eye className="h-4 w-4 text-white" />
                  </div>
                  Live Preview
                </h3>
                <Layout className="h-4 w-4 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mb-3">Updates as you type</p>
              
              {/* Template Selector */}
              <div className="flex gap-2 mb-3">
                {availableTemplates.map((template) => (
                <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.template_key)}
                  className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                      selectedTemplate === template.template_key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                    title={template.description || template.name}
                >
                    {template.name}
                </button>
                ))}
              </div>
            </div>
            <div
              className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-auto w-full"
              style={{
                maxHeight: 'calc(100vh - 150px)',
                minWidth: '280px'
              }}
            >
              <QuotationPreviewContent
                previewData={previewData}
                companyBranches={companyBranches}
                user={user}
                selectedTemplate={selectedTemplate}
                availableTemplates={availableTemplates}
              />
            </div>
          </div>
        </div>
      </div>
      </>
  )

  // If standalone mode, render without modal wrapper
  if (standalone) {
    return (
      <Card className="w-full max-h-screen overflow-y-auto shadow-lg">
        {formContent}
      </Card>
    )
  }

  // Otherwise render as modal
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[110] p-2 sm:p-4">
      <Card className="w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
        {formContent}
      </Card>
    </div>
  )
}
